import { Eye, EyeOff, type LucideIcon } from 'lucide-react';
import { type ComponentProps, useState } from 'react';
import { cn } from '../lib/cn';
import { useFieldControl } from './Field';

export const controlClasses = cn(
  'w-full min-w-0 rounded-md border border-line bg-canvas text-body-s text-fg placeholder:text-fg-muted',
  'transition-[border-color,box-shadow] duration-100 outline-none',
  'hover:border-line-strong focus:border-accent focus:shadow-[0_0_0_1px_var(--accent-primary)]',
  'aria-[invalid=true]:border-danger aria-[invalid=true]:focus:shadow-[0_0_0_1px_var(--status-danger)]',
  'disabled:cursor-not-allowed disabled:bg-surface disabled:text-fg-muted',
);

export const controlSizes = { s: 'h-8 px-2.5', m: 'h-10 px-3' } as const;

export interface InputProps extends Omit<ComponentProps<'input'>, 'size'> {
  size?: keyof typeof controlSizes;
  /** Ikona po lewej (np. Search). */
  icon?: LucideIcon;
  /** Wartość w foncie mono (czas, kwoty, kody). */
  mono?: boolean;
}

export function Input({ size = 'm', icon: Icon, mono = false, className, ...props }: InputProps) {
  const field = useFieldControl(props);
  const input = (
    <input
      {...props}
      {...field}
      className={cn(controlClasses, controlSizes[size], mono && 'font-mono font-medium', Icon && 'pl-9', className)}
    />
  );
  if (!Icon) return input;
  return (
    <div className="relative w-full">
      <Icon
        size={16}
        strokeWidth={1.75}
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-fg-muted"
      />
      {input}
    </div>
  );
}

/** Hasło z przełącznikiem „pokaż / ukryj”. */
export function PasswordInput({ size = 'm', className, ...props }: Omit<InputProps, 'type' | 'icon' | 'mono'>) {
  const [visible, setVisible] = useState(false);
  const field = useFieldControl(props);
  return (
    <div className="relative w-full">
      <input
        {...props}
        {...field}
        type={visible ? 'text' : 'password'}
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        className={cn(controlClasses, controlSizes[size], 'pr-10', className)}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Ukryj hasło' : 'Pokaż hasło'}
        aria-pressed={visible}
        aria-controls={field.id}
        className={cn(
          'absolute top-1/2 right-1.5 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-md text-fg-muted',
          'hover:bg-elevated hover:text-fg focus-visible:outline-2 focus-visible:outline-accent',
        )}
      >
        {visible ? (
          <EyeOff size={16} strokeWidth={1.75} aria-hidden />
        ) : (
          <Eye size={16} strokeWidth={1.75} aria-hidden />
        )}
      </button>
    </div>
  );
}

export function Textarea({ className, rows = 4, ...props }: ComponentProps<'textarea'>) {
  const field = useFieldControl(props);
  return (
    <textarea
      rows={rows}
      {...props}
      {...field}
      className={cn(controlClasses, 'resize-y px-3 py-2.5 leading-[1.5]', className)}
    />
  );
}
